import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Package, Plus, MapPin, DollarSign, ShieldCheck, Upload, X, Image, Loader2, Sparkles, Target, CheckCircle, Send, Edit, Trash2, RefreshCw, ShoppingBag, Building2, Lock, Video, Play, ChevronLeft, ChevronRight, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import CountrySelect from '@/components/CountrySelect';
import HSCodeSelect from '@/components/HSCodeSelect';
import PaymentTermsSelector, { PaymentTermsDisplay } from '@/components/PaymentTermsSelector';
import { useLanguage } from '@/context/LanguageContext';
import { FullyVerifiedBadge, PhoneVerifiedBadge, GSTVerifiedBadge, IECVerifiedBadge, PartialVerificationBadge } from '@/components/VerificationBadges';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Products() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [user, setUser] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // Translation state for product descriptions
  const [translatedDescriptions, setTranslatedDescriptions] = useState({});
  const [isTranslating, setIsTranslating] = useState({});
  
  // AI Smart Match state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSmartMatching, setIsSmartMatching] = useState(false);
  const [smartMatchResults, setSmartMatchResults] = useState(null);
  const [showSmartResults, setShowSmartResults] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const PRODUCTS_PER_PAGE = 24;
  
  // AI Description Generator state
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiModalForm, setAiModalForm] = useState({
    productName: '',
    productDetails: '',
    hsCode: ''
  });
  
  // Edit and Delete state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Currency conversion state (for Browse Marketplace consistency with /marketplace)
  const [currencyRates, setCurrencyRates] = useState(null);
  const [preferredCurrency, setPreferredCurrency] = useState('USD');

  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    currency: 'USD',
    min_order: '',
    unit: 'kg',
    origin_country: '',
    hs_code: '',
    hs_description: '',
    certifications: [],
    images: [],
    videos: [],
    specifications: {},
    // Payment terms
    payment_terms: [],
    custom_payment_terms: '',
    payment_terms_negotiable: true
  });

  // Predefined export market categories
  const exportCategories = [
    // Agriculture & Food
    { group: 'Agriculture & Food', items: [
      'Rice & Grains',
      'Spices & Herbs',
      'Tea & Coffee',
      'Fresh Fruits',
      'Fresh Vegetables',
      'Dry Fruits & Nuts',
      'Pulses & Lentils',
      'Sugar & Jaggery',
      'Edible Oils',
      'Processed Foods',
      'Seafood',
      'Meat Products',
      'Dairy Products',
      'Honey & Bee Products',
      'Organic Products'
    ]},
    // Textiles & Apparel
    { group: 'Textiles & Apparel', items: [
      'Cotton Textiles',
      'Silk Fabrics',
      'Wool & Woolen Products',
      'Synthetic Textiles',
      'Readymade Garments',
      'Home Textiles',
      'Technical Textiles',
      'Handloom Products',
      'Carpets & Rugs',
      'Leather Garments'
    ]},
    // Gems & Jewelry
    { group: 'Gems & Jewelry', items: [
      'Diamonds',
      'Gold Jewelry',
      'Silver Jewelry',
      'Precious Stones',
      'Semi-precious Stones',
      'Imitation Jewelry',
      'Pearls'
    ]},
    // Chemicals & Pharmaceuticals
    { group: 'Chemicals & Pharmaceuticals', items: [
      'Pharmaceuticals',
      'Ayurvedic Products',
      'Organic Chemicals',
      'Inorganic Chemicals',
      'Dyes & Pigments',
      'Agrochemicals',
      'Cosmetics & Personal Care',
      'Essential Oils'
    ]},
    // Engineering & Machinery
    { group: 'Engineering & Machinery', items: [
      'Auto Components',
      'Industrial Machinery',
      'Agricultural Machinery',
      'Pumps & Compressors',
      'Hand Tools',
      'Electrical Equipment',
      'Iron & Steel Products',
      'Aluminum Products',
      'Copper Products'
    ]},
    // Leather & Footwear
    { group: 'Leather & Footwear', items: [
      'Leather Footwear',
      'Leather Bags',
      'Leather Accessories',
      'Sports Goods',
      'Saddlery & Harness'
    ]},
    // Handicrafts & Home Decor
    { group: 'Handicrafts & Home Decor', items: [
      'Wooden Handicrafts',
      'Metal Handicrafts',
      'Brass Artware',
      'Marble Products',
      'Pottery & Ceramics',
      'Cane & Bamboo Products',
      'Paper Products',
      'Decorative Items'
    ]},
    // IT & Electronics
    { group: 'IT & Electronics', items: [
      'Software Services',
      'Electronic Components',
      'Consumer Electronics',
      'Computer Hardware',
      'Telecom Equipment',
      'Medical Electronics'
    ]},
    // Plastics & Rubber
    { group: 'Plastics & Rubber', items: [
      'Plastic Products',
      'Rubber Products',
      'Tyres & Tubes',
      'Plastic Raw Materials'
    ]},
    // Marine & Seafood
    { group: 'Marine & Seafood', items: [
      'Frozen Shrimp',
      'Fish & Fish Products',
      'Crab & Lobster',
      'Dried Fish',
      'Fish Meal'
    ]},
    // Others
    { group: 'Other Industries', items: [
      'Paper & Paper Products',
      'Glass & Glassware',
      'Cement & Building Materials',
      'Furniture',
      'Sports Equipment',
      'Toys & Games',
      'Musical Instruments',
      'Other'
    ]}
  ];

  // Common specification fields based on category
  const [specFields, setSpecFields] = useState([
    { key: '', value: '' }
  ]);

  const [inquiryForm, setInquiryForm] = useState({
    message: '',
    quantity: '',
    images: [],
    // Payment and trade details
    estimated_order_value: '',
    currency: 'USD',
    preferred_payment_terms: [],
    proposed_payment_terms: '',
    incoterm: '',
    destination_country: '',
    required_delivery_date: '',
    sample_required: false,
    inspection_required: false
  });
  
  const [inquiryImagePreviews, setInquiryImagePreviews] = useState([]);
  const [isUploadingInquiryImage, setIsUploadingInquiryImage] = useState(false);
  const inquiryFileInputRef = useRef(null);

  const commonSpecSuggestions = {
    'Textiles': ['GSM', 'Material', 'Width', 'Color', 'Pattern', 'Thread Count'],
    'Food': ['Grade', 'Moisture Content', 'Purity', 'Shelf Life', 'Packaging'],
    'Electronics': ['Voltage', 'Wattage', 'Dimensions', 'Weight', 'Warranty'],
    'Machinery': ['Power', 'Capacity', 'Dimensions', 'Weight', 'Motor Type'],
    'Chemicals': ['Purity', 'Grade', 'pH Level', 'Density', 'Flash Point'],
    'default': ['Size', 'Weight', 'Material', 'Color', 'Grade']
  };

  // Currency symbol helper
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'AED': 'د.إ',
      'SAR': '﷼',
      'SGD': 'S$',
      'JPY': '¥',
      'CNY': '¥',
      'AUD': 'A$'
    };
    return symbols[currency] || currency + ' ';
  };

  // Tab state for sellers - "my-products" or "marketplace"
  const [activeTab, setActiveTab] = useState('marketplace'); // Default to marketplace for better UX
  const [marketplaceProducts, setMarketplaceProducts] = useState([]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    if (userData?.role === 'seller') {
      fetchMyProducts();
      fetchMarketplaceProducts(); // Also fetch marketplace for sellers
    } else {
      fetchProducts();
    }
    
    // Load preferred currency from localStorage
    const savedCurrency = localStorage.getItem('preferredCurrency') || 'USD';
    setPreferredCurrency(savedCurrency);
    
    // Fetch currency rates for conversion
    fetchCurrencyRates();
  }, []);

  const fetchCurrencyRates = async () => {
    try {
      const response = await axios.get(`${API}/currency/rates?base=INR`);
      setCurrencyRates(response.data.rates);
    } catch (error) {
      console.error('Failed to fetch currency rates');
    }
  };

  // Convert price to preferred currency
  const convertPrice = (price, targetCurrency, sourceCurrency = 'INR') => {
    if (!currencyRates || !price) return null;
    
    let priceInINR = price;
    if (sourceCurrency !== 'INR' && currencyRates[sourceCurrency]) {
      priceInINR = price / currencyRates[sourceCurrency];
    }
    
    const rate = currencyRates[targetCurrency];
    if (!rate) return null;
    return (priceInINR * rate).toFixed(2);
  };

  const fetchMyProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products/my-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle paginated response
      const data = response.data;
      setProducts(data.products || []);
    } catch (error) {
      toast.error('Failed to fetch your products');
    }
  };

  const fetchMarketplaceProducts = async (page = 1) => {
    try {
      // Fetch with pagination - public endpoint, no auth needed
      const response = await axios.get(`${API}/products?include_media=true&page=${page}&limit=${PRODUCTS_PER_PAGE}`);
      const data = response.data;
      const pagination = data.pagination || {};
      const productsList = data.products || data || [];
      setMarketplaceProducts(productsList);
      setCurrentPage(pagination.page || 1);
      setTotalPages(pagination.total_pages || 1);
      setTotalProducts(pagination.total || productsList.length);
    } catch (error) {
      console.error('Failed to fetch marketplace products:', error);
      toast.error('Failed to load marketplace products. Please refresh the page.');
    }
  };

  const fetchProducts = async (page = 1) => {
    try {
      // Fetch with pagination - public endpoint, no auth needed
      const response = await axios.get(`${API}/products?include_media=true&page=${page}&limit=${PRODUCTS_PER_PAGE}`);
      const data = response.data;
      const pagination = data.pagination || {};
      const productsList = data.products || data || [];
      setProducts(productsList);
      setCurrentPage(pagination.page || 1);
      setTotalPages(pagination.total_pages || 1);
      setTotalProducts(pagination.total || productsList.length);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products. Please refresh the page.');
    }
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      if (user?.role === 'seller' && activeTab === 'marketplace') {
        fetchMarketplaceProducts(newPage);
      } else {
        fetchProducts(newPage);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    Array.from(files).forEach(file => {
      // Check file size (max 5MB per image)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} must be less than 5MB`);
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result;
        setImagePreviews(prev => [...prev, base64]);
        setProductForm(prev => ({
          ...prev,
          images: [...prev.images, base64]
        }));
      };
      reader.readAsDataURL(file);
    });
    
    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleVideoUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploadingVideo(true);
    
    Array.from(files).forEach(file => {
      // Check file size (max 50MB per video)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} must be less than 50MB`);
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('video/')) {
        toast.error(`${file.name} is not a video file`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result;
        setVideoPreviews(prev => [...prev, base64]);
        setProductForm(prev => ({
          ...prev,
          videos: [...prev.videos, base64]
        }));
      };
      reader.readAsDataURL(file);
    });
    
    setIsUploadingVideo(false);
    
    // Clear the input
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const removeVideo = (index) => {
    setVideoPreviews(prev => prev.filter((_, i) => i !== index));
    setProductForm(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  // Translate descriptions when language changes
  useEffect(() => {
    const translateDescriptions = async () => {
      if (products.length === 0) return;
      
      const currentLang = language || 'en';
      if (currentLang === 'en') {
        setTranslatedDescriptions({});
        return;
      }

      for (const product of products) {
        if (!product.description || translatedDescriptions[`${product.id}_${currentLang}`]) continue;
        
        setIsTranslating(prev => ({ ...prev, [product.id]: true }));
        
        try {
          const response = await axios.post(`${API}/ai/translate`, {
            text: product.description,
            target_language: currentLang,
            context: 'product_description'
          });
          
          setTranslatedDescriptions(prev => ({
            ...prev,
            [`${product.id}_${currentLang}`]: response.data.translated_text
          }));
        } catch (error) {
          console.error('Translation failed for product:', product.id);
        } finally {
          setIsTranslating(prev => ({ ...prev, [product.id]: false }));
        }
      }
    };

    translateDescriptions();
  }, [language, products]);

  // Helper function to get translated description
  const getTranslatedDescription = (product) => {
    const currentLang = language || 'en';
    if (currentLang === 'en') return product.description;
    
    const translatedKey = `${product.id}_${currentLang}`;
    return translatedDescriptions[translatedKey] || product.description;
  };

  // Edit Product
  const handleEditProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build specifications object
      const specifications = {};
      specFields.forEach(field => {
        if (field.key && field.value) {
          specifications[field.key] = field.value;
        }
      });

      await axios.put(`${API}/products/${editingProduct.id}`, {
        ...productForm,
        price: parseFloat(productForm.price),
        min_order: parseInt(productForm.min_order),
        specifications: Object.keys(specifications).length > 0 ? specifications : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Product updated successfully');
      setShowEditDialog(false);
      setEditingProduct(null);
      resetProductForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete Product
  const handleDeleteProduct = async () => {
    if (!editingProduct) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/${editingProduct.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Product deleted successfully');
      setShowDeleteDialog(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    } finally {
      setIsDeleting(false);
    }
  };

  // Open Edit Dialog with product data
  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title || '',
      description: product.description || '',
      category: product.category || '',
      price: product.price?.toString() || '',
      currency: product.currency || 'INR',
      min_order: product.min_order?.toString() || '',
      unit: product.unit || 'kg',
      origin_country: product.origin_country || '',
      hs_code: product.hs_code || '',
      hs_description: product.hs_description || '',
      certifications: product.certifications || [],
      images: product.images || [],
      videos: product.videos || [],
      specifications: product.specifications || {}
    });
    
    // Set spec fields from product specifications
    if (product.specifications && Object.keys(product.specifications).length > 0) {
      setSpecFields(
        Object.entries(product.specifications).map(([key, value]) => ({ key, value }))
      );
    } else {
      setSpecFields([{ key: '', value: '' }]);
    }
    
    // Set image and video previews
    setImagePreviews(product.images || []);
    setVideoPreviews(product.videos || []);
    setShowEditDialog(true);
  };

  // Reset product form
  const resetProductForm = () => {
    setProductForm({
      title: '',
      description: '',
      category: '',
      price: '',
      min_order: '',
      unit: 'kg',
      origin_country: '',
      hs_code: '',
      hs_description: '',
      certifications: [],
      images: [],
      videos: [],
      specifications: {},
      payment_terms: [],
      custom_payment_terms: '',
      payment_terms_negotiable: true
    });
    setSpecFields([{ key: '', value: '' }]);
    setImagePreviews([]);
    setVideoPreviews([]);
    setAiSuggestions(null);
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Build specifications object from specFields
    const specifications = {};
    specFields.forEach(field => {
      if (field.key && field.value) {
        specifications[field.key] = field.value;
      }
    });

    try {
      await axios.post(`${API}/products`, {
        ...productForm,
        price: parseFloat(productForm.price),
        min_order: parseInt(productForm.min_order),
        specifications: Object.keys(specifications).length > 0 ? specifications : null,
        payment_terms: productForm.payment_terms,
        custom_payment_terms: productForm.custom_payment_terms || null,
        payment_terms_negotiable: productForm.payment_terms_negotiable
      });
      toast.success('Product added successfully');
      setShowAddDialog(false);
      setProductForm({
        title: '',
        description: '',
        category: '',
        price: '',
        currency: 'USD',
        min_order: '',
        unit: 'kg',
        origin_country: '',
        hs_code: '',
        hs_description: '',
        certifications: [],
        images: [],
        videos: [],
        specifications: {},
        payment_terms: [],
        custom_payment_terms: '',
        payment_terms_negotiable: true
      });
      setSpecFields([{ key: '', value: '' }]);
      setImagePreviews([]);
      setVideoPreviews([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInquiryImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Limit to 3 images
    if (inquiryForm.images.length + files.length > 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    setIsUploadingInquiryImage(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB per image.`);
        continue;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }

      try {
        // Convert to base64
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Upload to server
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/inquiry-images/upload`,
          { image: base64 },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Add to form
        setInquiryForm(prev => ({
          ...prev,
          images: [...prev.images, response.data.url]
        }));
        setInquiryImagePreviews(prev => [...prev, base64]);
        
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setIsUploadingInquiryImage(false);
    if (inquiryFileInputRef.current) {
      inquiryFileInputRef.current.value = '';
    }
  };

  const removeInquiryImage = (index) => {
    setInquiryForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setInquiryImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleInquiry = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/inquiries`, {
        product_id: selectedProduct.id,
        message: inquiryForm.message,
        quantity: parseInt(inquiryForm.quantity),
        images: inquiryForm.images,
        // Payment and trade details
        estimated_order_value: inquiryForm.estimated_order_value ? parseFloat(inquiryForm.estimated_order_value) : null,
        currency: inquiryForm.currency,
        preferred_payment_terms: inquiryForm.preferred_payment_terms,
        proposed_payment_terms: inquiryForm.proposed_payment_terms || null,
        incoterm: inquiryForm.incoterm || null,
        destination_country: inquiryForm.destination_country || null,
        required_delivery_date: inquiryForm.required_delivery_date || null,
        sample_required: inquiryForm.sample_required,
        inspection_required: inquiryForm.inspection_required
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inquiry sent successfully! AI response generated.');
      setShowInquiryDialog(false);
      setInquiryForm({ 
        message: '', 
        quantity: '', 
        images: [],
        estimated_order_value: '',
        currency: 'USD',
        preferred_payment_terms: [],
        proposed_payment_terms: '',
        incoterm: '',
        destination_country: '',
        required_delivery_date: '',
        sample_required: false,
        inspection_required: false
      });
      setInquiryImagePreviews([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setIsLoading(false);
    }
  };

  // AI Smart Match handler
  const handleSmartSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter what you're looking for");
      return;
    }
    
    setIsSmartMatching(true);
    setShowSmartResults(false);
    
    try {
      const response = await axios.post(`${API}/ai/smart-match`, {
        query: searchQuery
      });
      
      setSmartMatchResults(response.data);
      setShowSmartResults(true);
      
      if (response.data.matches?.length === 0) {
        toast.info('No exact matches found. Showing all products.');
        setShowSmartResults(false);
      }
    } catch (error) {
      toast.error('Smart matching failed. Showing regular results.');
      setShowSmartResults(false);
    } finally {
      setIsSmartMatching(false);
    }
  };

  const clearSmartResults = () => {
    setShowSmartResults(false);
    setSmartMatchResults(null);
    setSearchQuery('');
  };

  // AI Description Generator - Modal based
  const openAiModal = () => {
    setAiModalForm({
      productName: productForm.title || '',
      productDetails: '',
      hsCode: productForm.hs_code || ''
    });
    setAiSuggestions(null);
    setShowAiModal(true);
  };

  const handleGenerateFromModal = async () => {
    if (!aiModalForm.productName || !aiModalForm.productDetails) {
      toast.error('Please enter product name and details');
      return;
    }

    setIsGeneratingDescription(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to use AI features');
        return;
      }

      const response = await axios.post(`${API}/ai/generate-description`, {
        title: aiModalForm.productName,
        category: productForm.category || 'General',
        specifications: aiModalForm.productDetails ? { details: aiModalForm.productDetails } : null,
        origin_country: productForm.origin_country,
        hs_code: aiModalForm.hsCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAiSuggestions(response.data);
      
      // Auto-fill the description in the main form
      if (response.data.description) {
        setProductForm(prev => ({
          ...prev,
          title: aiModalForm.productName,
          description: response.data.description,
          hs_code: aiModalForm.hsCode || prev.hs_code
        }));
        toast.success('AI generated description! You can edit it below.');
      }
    } catch (error) {
      console.error('AI generation error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to generate description');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleRegenerateDescription = () => {
    setAiSuggestions(null);
    handleGenerateFromModal();
  };

  const handleApplyAiDescription = () => {
    if (aiSuggestions?.description) {
      setProductForm(prev => ({
        ...prev,
        title: aiModalForm.productName || prev.title,
        description: aiSuggestions.description
      }));
      setShowAiModal(false);
      toast.success('Description applied! You can edit it in the form.');
    }
  };

  // Legacy handler for inline button (keeping for backward compatibility)
  const handleGenerateDescription = async () => {
    if (!productForm.title || !productForm.category) {
      toast.error('Please enter product title and category first');
      return;
    }

    setIsGeneratingDescription(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to use AI features');
        return;
      }
      
      const specs = {};
      specFields.forEach(field => {
        if (field.key && field.value) {
          specs[field.key] = field.value;
        }
      });

      const response = await axios.post(`${API}/ai/generate-description`, {
        title: productForm.title,
        category: productForm.category,
        specifications: Object.keys(specs).length > 0 ? specs : null,
        origin_country: productForm.origin_country
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAiSuggestions(response.data);
      
      // Auto-fill the description
      if (response.data.description) {
        setProductForm(prev => ({
          ...prev,
          description: response.data.description
        }));
        toast.success('AI generated description! Review and edit as needed.');
      } else {
        toast.info('AI response received but no description generated');
      }
    } catch (error) {
      console.error('AI generation error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to generate description');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Filter products based on search when not using AI
  // For sellers, show different products based on active tab
  const getDisplayProducts = () => {
    if (user?.role === 'seller') {
      if (activeTab === 'marketplace') {
        return marketplaceProducts;
      }
      return products; // My products
    }
    // For buyers
    if (showSmartResults && smartMatchResults?.matches?.length > 0) {
      return smartMatchResults.matches.map(m => ({ ...m.product, matchInfo: m }));
    }
    return products;
  };

  const displayProducts = getDisplayProducts();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="products-page">
        {/* AI Smart Match Section - Available for all logged-in users */}
        {(user?.role === 'buyer' || (user?.role === 'seller' && activeTab === 'marketplace')) && (
          <div className="mb-8 bg-gradient-to-r from-primary to-blue-700 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-6 w-6 text-amber-400" />
              <h2 className="font-heading text-xl font-bold">AI Smart Match</h2>
            </div>
            <p className="text-blue-100 mb-4 text-sm">
              Describe what you need in natural language - our AI will find the best matching products
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-amber-400" />
                <Input
                  placeholder="Try: '4 tons of basmati rice' or 'cotton fabric 5000 meters'"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSmartSearch()}
                  className="pl-10 h-12 text-slate-900 bg-white"
                  data-testid="products-ai-search"
                />
              </div>
              <Button 
                onClick={handleSmartSearch}
                disabled={isSmartMatching}
                size="lg" 
                className="bg-amber-500 hover:bg-amber-600 h-12 px-6"
              >
                {isSmartMatching ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Find Matches
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Smart Match Results Banner */}
        {showSmartResults && smartMatchResults && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-amber-900">AI Smart Match Results</span>
                </div>
                {smartMatchResults.parsed_query && (
                  <p className="text-sm text-amber-700 mt-1">
                    Looking for: <span className="font-medium">{smartMatchResults.parsed_query.product_type}</span>
                    {smartMatchResults.parsed_query.quantity && (
                      <span className="font-medium"> ({smartMatchResults.parsed_query.quantity.toLocaleString()} {smartMatchResults.parsed_query.unit || 'units'})</span>
                    )}
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={clearSmartResults}>
                Clear & Show All
              </Button>
            </div>
          </div>
        )}

        {/* Seller Tab Navigation */}
        {user?.role === 'seller' && (
          <div className="flex gap-2 mb-6 border-b">
            <button
              onClick={() => setActiveTab('my-products')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'my-products'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-my-products"
            >
              <Package className="h-4 w-4 inline mr-2" />
              My Products ({products.length})
            </button>
            <button
              onClick={() => setActiveTab('marketplace')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'marketplace'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-marketplace"
            >
              <ShoppingBag className="h-4 w-4 inline mr-2" />
              Browse Marketplace ({marketplaceProducts.length})
            </button>
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary mb-2">
              {user?.role === 'buyer' 
                ? 'Product Marketplace' 
                : activeTab === 'my-products'
                  ? `Your Products (${products.length} total)`
                  : 'Browse Marketplace'}
            </h1>
            <p className="text-muted-foreground">
              {user?.role === 'buyer' 
                ? 'Discover products from verified suppliers worldwide' 
                : activeTab === 'my-products'
                  ? 'Manage your product listings - Edit or delete products below'
                  : 'See what other sellers are offering in the marketplace'}
            </p>
          </div>
          {user?.role === 'seller' && activeTab === 'my-products' && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-product-trigger-btn">
                  <Plus className="h-4 w-4" />
                  Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add New Product</DialogTitle>
                  <DialogDescription>Fill in the details to list your product</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddProduct} className="space-y-4">
                  {/* Image Upload */}
                  <div>
                    <Label>Product Images</Label>
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-3 mb-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative">
                            <img 
                              src={preview} 
                              alt={`Preview ${index + 1}`} 
                              className="w-24 h-24 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                            {index === 0 && (
                              <span className="absolute bottom-1 left-1 bg-primary text-white text-xs px-1 rounded">
                                Main
                              </span>
                            )}
                          </div>
                        ))}
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                        >
                          <Image className="h-6 w-6 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground text-center">Add Image</span>
                        </div>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        multiple
                        data-testid="product-image-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        First image will be the main display image. Max 5MB each, JPG/PNG recommended.
                        {imagePreviews.length > 0 && ` (${imagePreviews.length} image${imagePreviews.length > 1 ? 's' : ''} added)`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Video Upload */}
                  <div>
                    <Label>Product Videos (Optional)</Label>
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-3 mb-3">
                        {videoPreviews.map((preview, index) => (
                          <div key={index} className="relative">
                            <video 
                              src={preview} 
                              className="w-32 h-24 object-cover rounded-lg border bg-black"
                              muted
                            />
                            <button
                              type="button"
                              onClick={() => removeVideo(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Play className="h-8 w-8 text-white/80" />
                            </div>
                          </div>
                        ))}
                        <div 
                          onClick={() => videoInputRef.current?.click()}
                          className="w-32 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                        >
                          <Video className="h-6 w-6 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground text-center">Add Video</span>
                        </div>
                      </div>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        className="hidden"
                        multiple
                        data-testid="product-video-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Max 50MB per video, MP4/MOV recommended.
                        {videoPreviews.length > 0 && ` (${videoPreviews.length} video${videoPreviews.length > 1 ? 's' : ''} added)`}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="title">Product Title</Label>
                    <Input
                      id="title"
                      value={productForm.title}
                      onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                      required
                      data-testid="product-title-input"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="description">Description</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openAiModal}
                        className="text-xs gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 hover:from-amber-600 hover:to-orange-600"
                        data-testid="ai-generate-description-btn"
                      >
                        <Sparkles className="h-3 w-3" />
                        AI Generate Description
                      </Button>
                    </div>
                    <Textarea
                      id="description"
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      required
                      placeholder="Describe your product... or click 'AI Generate Description' for help"
                      data-testid="product-description-input"
                      rows={4}
                    />
                    {aiSuggestions && !showAiModal && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1">
                          <Sparkles className="h-3 w-3" />
                          AI Suggestions
                        </p>
                        {aiSuggestions.suggested_specs?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs text-amber-700 font-medium">Consider adding these specs:</p>
                            <ul className="text-xs text-amber-600 mt-1 space-y-0.5">
                              {aiSuggestions.suggested_specs.slice(0, 3).map((spec, idx) => (
                                <li key={idx}>• {spec.name}: {spec.description}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aiSuggestions.keywords?.length > 0 && (
                          <p className="text-xs text-amber-600">
                            <span className="font-medium">Keywords:</span> {aiSuggestions.keywords.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>HS Code (Trade Classification)</Label>
                    <HSCodeSelect
                      value={productForm.hs_code ? { code: productForm.hs_code, description: productForm.hs_description } : null}
                      onChange={(code, description) => setProductForm({ ...productForm, hs_code: code, hs_description: description })}
                      placeholder="Search HS code (e.g., rice, coffee, textiles)"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select 
                        value={productForm.category} 
                        onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                      >
                        <SelectTrigger data-testid="product-category-input">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {exportCategories.map((group) => (
                            <div key={group.group}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                                {group.group}
                              </div>
                              {group.items.map((item) => (
                                <SelectItem key={item} value={item}>
                                  {item}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Origin Country</Label>
                      <CountrySelect
                        value={productForm.origin_country}
                        onChange={(name) => setProductForm({ ...productForm, origin_country: name })}
                        placeholder="Select origin country"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="price">Price</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={productForm.price}
                        onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                        required
                        data-testid="product-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="currency">Currency</Label>
                      <Select
                        value={productForm.currency}
                        onValueChange={(value) => setProductForm({ ...productForm, currency: value })}
                      >
                        <SelectTrigger data-testid="product-currency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="EUR">EUR (€)</SelectItem>
                          <SelectItem value="GBP">GBP (£)</SelectItem>
                          <SelectItem value="INR">INR (₹)</SelectItem>
                          <SelectItem value="AED">AED (د.إ)</SelectItem>
                          <SelectItem value="SAR">SAR (﷼)</SelectItem>
                          <SelectItem value="SGD">SGD (S$)</SelectItem>
                          <SelectItem value="JPY">JPY (¥)</SelectItem>
                          <SelectItem value="CNY">CNY (¥)</SelectItem>
                          <SelectItem value="AUD">AUD (A$)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="min-order">Min Order</Label>
                      <Input
                        id="min-order"
                        type="number"
                        value={productForm.min_order}
                        onChange={(e) => setProductForm({ ...productForm, min_order: e.target.value })}
                        required
                        data-testid="product-min-order-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="unit">Unit</Label>
                      <Select
                        value={productForm.unit}
                        onValueChange={(value) => setProductForm({ ...productForm, unit: value })}
                      >
                        <SelectTrigger data-testid="product-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="ton">ton</SelectItem>
                          <SelectItem value="unit">unit</SelectItem>
                          <SelectItem value="box">box</SelectItem>
                          <SelectItem value="pcs">pcs</SelectItem>
                          <SelectItem value="liter">liter</SelectItem>
                          <SelectItem value="m3">m³</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Payment Terms */}
                  <div className="border rounded-lg p-4 space-y-3 bg-slate-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="h-4 w-4 text-blue-600" />
                      <Label className="text-base font-semibold">Payment Terms</Label>
                    </div>
                    <PaymentTermsSelector
                      value={productForm.payment_terms}
                      onChange={(terms) => setProductForm({ ...productForm, payment_terms: terms })}
                      customTerms={productForm.custom_payment_terms}
                      onCustomTermsChange={(text) => setProductForm({ ...productForm, custom_payment_terms: text })}
                      negotiable={productForm.payment_terms_negotiable}
                      onNegotiableChange={(checked) => setProductForm({ ...productForm, payment_terms_negotiable: checked })}
                      showSafetyNotice={true}
                      maxSelections={5}
                      label=""
                      placeholder="Select accepted payment terms..."
                      role="seller"
                    />
                  </div>
                  
                  {/* Product Specifications */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Product Specifications</Label>
                      <span className="text-xs text-muted-foreground">e.g., GSM, Material, Weight</span>
                    </div>
                    {specFields.map((field, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          placeholder="Spec name (e.g., GSM)"
                          value={field.key}
                          onChange={(e) => {
                            const newFields = [...specFields];
                            newFields[idx].key = e.target.value;
                            setSpecFields(newFields);
                          }}
                          className="flex-1"
                          list={`spec-suggestions-${idx}`}
                        />
                        <datalist id={`spec-suggestions-${idx}`}>
                          {(commonSpecSuggestions[productForm.category] || commonSpecSuggestions['default']).map(s => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                        <Input
                          placeholder="Value (e.g., 180)"
                          value={field.value}
                          onChange={(e) => {
                            const newFields = [...specFields];
                            newFields[idx].value = e.target.value;
                            setSpecFields(newFields);
                          }}
                          className="flex-1"
                        />
                        {specFields.length > 1 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setSpecFields(specFields.filter((_, i) => i !== idx))}
                          >
                            ×
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSpecFields([...specFields, { key: '', value: '' }])}
                    >
                      + Add Specification
                    </Button>
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="submit-product-btn">
                    {isLoading ? 'Adding...' : 'Add Product'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Empty state message */}
        {displayProducts.length === 0 && !showSmartResults && (
          <div className="text-center py-12 px-4">
            <Package className="h-16 w-16 mx-auto text-slate-400 mb-4" />
            {activeTab === 'my-products' ? (
              <>
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No products listed yet</h3>
                <p className="text-slate-500 mb-4">Start by adding your first product to get discovered by international buyers</p>
                <Button onClick={() => setShowAddDialog(true)} className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Product
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No products found</h3>
                <p className="text-slate-500">Try adjusting your search or filters</p>
              </>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayProducts.map((product) => (
            <Card 
              key={product.id} 
              className="product-card overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" 
              data-testid={`product-card-${product.id}`}
              onClick={() => {
                // Navigate to product detail page when card is clicked (except when clicking buttons)
                if (activeTab === 'marketplace' || user?.role === 'buyer') {
                  navigate(`/product/${product.id}`);
                }
              }}
            >
              {/* Product Image */}
              <div className="h-40 bg-gradient-to-br from-slate-100 to-slate-200 relative">
                {product.images && product.images.length > 0 ? (
                  <img 
                    src={product.images[0]} 
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="h-12 w-12 text-slate-400" />
                  </div>
                )}
                
                {/* Match Score Badge for AI results */}
                {product.matchInfo && (
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    <Badge className={`${product.matchInfo.recommended ? 'bg-green-500' : 'bg-amber-500'} text-white`}>
                      <Target className="h-3 w-3 mr-1" />
                      {product.matchInfo.match_score}% Match
                    </Badge>
                    {product.matchInfo.recommended && (
                      <Badge className="bg-blue-600 text-white text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Recommended
                      </Badge>
                    )}
                  </div>
                )}
                
                {product.seller_verified && (
                  <Badge className="absolute top-2 right-2 bg-green-600 text-white">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
                
                {/* New Listing Badge - Shows for products created within 7 days */}
                {(() => {
                  const createdAt = new Date(product.created_at);
                  const now = new Date();
                  const daysDiff = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
                  return daysDiff <= 7;
                })() && (
                  <Badge className={`absolute ${product.seller_verified ? 'top-10' : 'top-2'} right-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white animate-pulse`}>
                    <Sparkles className="h-3 w-3 mr-1" />
                    New
                  </Badge>
                )}
              </div>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="font-heading text-lg line-clamp-1">{product.title}</CardTitle>
                </div>
                {/* Seller info with verification badges - matching Marketplace design */}
                <CardDescription>
                  <span 
                    className="flex items-center gap-1 hover:text-primary cursor-pointer"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      if (product.seller_id) {
                        navigate(`/seller/${product.seller_id}`);
                      }
                    }}
                  >
                    {product.company_logo ? (
                      <img 
                        src={product.company_logo} 
                        alt={product.seller_name}
                        className="h-5 w-5 rounded-full object-cover flex-shrink-0 border border-gray-200"
                      />
                    ) : (
                      <Building2 className="h-3 w-3 flex-shrink-0" />
                    )}
                    <span className="truncate hover:underline">{product.seller_name}</span>
                    {/* Verified badge for fully verified sellers */}
                    <FullyVerifiedBadge 
                      phoneVerified={product.phone_verified}
                      gstinVerified={product.gstin_verified}
                      iecStatus={product.iec_status}
                      size="small"
                      variant="compact"
                    />
                  </span>
                </CardDescription>
                {/* Location */}
                {product.origin_country && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" />
                    {product.origin_country}
                  </p>
                )}
                
                {/* Verification Badges - matching Marketplace design */}
                <div className="mt-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <PhoneVerifiedBadge verified={product.phone_verified} size="small" />
                    <GSTVerifiedBadge verified={product.gstin_verified} size="small" />
                    <IECVerifiedBadge status={product.iec_status} size="small" />
                    <PartialVerificationBadge 
                      phoneVerified={product.phone_verified}
                      gstinVerified={product.gstin_verified}
                      iecStatus={product.iec_status}
                      size="small"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Description */}
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {isTranslating[product.id] ? (
                      <span className="flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Translating...
                      </span>
                    ) : (
                      getTranslatedDescription(product)
                    )}
                  </p>
                  
                  {/* Match Reasons for AI results */}
                  {product.matchInfo?.reasons && product.matchInfo.reasons.length > 0 && (
                    <div className="p-2 bg-green-50 rounded-lg border border-green-100">
                      <p className="text-xs font-medium text-green-800 mb-1 flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        Why this matches:
                      </p>
                      <ul className="text-xs text-green-700 space-y-0.5">
                        {product.matchInfo.reasons.slice(0, 3).map((reason, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <span className="font-semibold text-green-800">{idx + 1}.</span>
                            <span>{reason.replace(/^Reason \d+:\s*/i, '').replace(/^\d+\.\s*/, '')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Price with currency conversion - matching Marketplace */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xl font-bold text-primary">
                        {getCurrencySymbol(product.currency || 'INR')}{product.price?.toLocaleString()}
                      </p>
                      {/* Currency conversion for buyers */}
                      {currencyRates && preferredCurrency !== (product.currency || 'INR') && (
                        <p className="text-xs text-muted-foreground">
                          ≈ {preferredCurrency} {convertPrice(product.price, preferredCurrency, product.currency)}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        per {product.unit || 'unit'}
                      </p>
                    </div>
                    {product.origin_country && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {product.origin_country}
                      </div>
                    )}
                  </div>
                  
                  {/* Specifications preview */}
                  {product.specifications && Object.keys(product.specifications).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(product.specifications).slice(0, 2).map(([key, value]) => (
                        <Badge key={key} variant="outline" className="text-xs">
                          {key}: {value}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {user?.role === 'buyer' && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
                        data-testid={`view-details-btn-${product.id}`}
                      >
                        View Details
                      </Button>
                      <Button
                        className="flex-1"
                        variant="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProduct(product);
                          setShowInquiryDialog(true);
                        }}
                        data-testid={`inquiry-btn-${product.id}`}
                      >
                        Send Inquiry
                      </Button>
                    </div>
                  )}
                  {/* Sellers viewing Browse Marketplace (other sellers' products) */}
                  {user?.role === 'seller' && activeTab === 'marketplace' && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
                        data-testid={`view-details-btn-${product.id}`}
                      >
                        View Details
                      </Button>
                      <Button
                        className="flex-1"
                        variant="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProduct(product);
                          setShowInquiryDialog(true);
                        }}
                        data-testid={`inquiry-btn-${product.id}`}
                      >
                        Send Inquiry
                      </Button>
                    </div>
                  )}
                  {user?.role === 'seller' && activeTab === 'my-products' && product.seller_id === user.id && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => { e.stopPropagation(); openEditDialog(product); }}
                        data-testid={`edit-btn-${product.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingProduct(product);
                          setShowDeleteDialog(true);
                        }}
                        data-testid={`delete-btn-${product.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && !showSmartResults && (
          <div className="flex items-center justify-center gap-2 mt-8 pb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="hidden sm:flex"
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Previous</span>
            </Button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="hidden sm:flex"
            >
              Last
            </Button>
          </div>
        )}

        {/* Product Count Info */}
        {totalProducts > 0 && (
          <div className="text-center text-sm text-muted-foreground mb-4">
            Showing {displayProducts.length} of {totalProducts} products
            {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
          </div>
        )}

        <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Send Inquiry</DialogTitle>
              <DialogDescription>
                Request a quote for {selectedProduct?.title}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInquiry} className="space-y-4">
              <div>
                <Label htmlFor="quantity">Quantity ({selectedProduct?.unit})</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={inquiryForm.quantity}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, quantity: e.target.value })}
                  required
                  data-testid="inquiry-quantity-input"
                />
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={inquiryForm.message}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                  placeholder="Additional requirements or questions..."
                  required
                  data-testid="inquiry-message-input"
                />
              </div>
              
              {/* Image Upload Section */}
              <div>
                <Label>Reference Images (Optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Upload up to 3 images to show what you're looking for
                </p>
                
                {/* Image Previews */}
                {inquiryImagePreviews.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {inquiryImagePreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={preview} 
                          alt={`Reference ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => removeInquiryImage(index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                {inquiryForm.images.length < 3 && (
                  <div>
                    <input
                      type="file"
                      ref={inquiryFileInputRef}
                      onChange={handleInquiryImageUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                      data-testid="inquiry-image-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => inquiryFileInputRef.current?.click()}
                      disabled={isUploadingInquiryImage}
                      className="w-full"
                    >
                      {isUploadingInquiryImage ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Add Reference Images
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Payment & Trade Details Section */}
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                  <Label className="text-base font-semibold">Payment & Trade Details</Label>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="estimated_value">Estimated Order Value</Label>
                    <Input
                      id="estimated_value"
                      type="number"
                      step="0.01"
                      value={inquiryForm.estimated_order_value}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, estimated_order_value: e.target.value })}
                      placeholder="e.g., 10000"
                      data-testid="inquiry-order-value-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inquiry_currency">Currency</Label>
                    <Select
                      value={inquiryForm.currency}
                      onValueChange={(value) => setInquiryForm({ ...inquiryForm, currency: value })}
                    >
                      <SelectTrigger data-testid="inquiry-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                        <SelectItem value="INR">INR (₹)</SelectItem>
                        <SelectItem value="AED">AED (د.إ)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="incoterm">Incoterm</Label>
                    <Select
                      value={inquiryForm.incoterm}
                      onValueChange={(value) => setInquiryForm({ ...inquiryForm, incoterm: value })}
                    >
                      <SelectTrigger data-testid="inquiry-incoterm-select">
                        <SelectValue placeholder="Select incoterm" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EXW">EXW - Ex Works</SelectItem>
                        <SelectItem value="FOB">FOB - Free on Board</SelectItem>
                        <SelectItem value="CIF">CIF - Cost, Insurance & Freight</SelectItem>
                        <SelectItem value="CFR">CFR - Cost and Freight</SelectItem>
                        <SelectItem value="DDP">DDP - Delivered Duty Paid</SelectItem>
                        <SelectItem value="DAP">DAP - Delivered at Place</SelectItem>
                        <SelectItem value="FCA">FCA - Free Carrier</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="destination">Destination Country</Label>
                    <CountrySelect
                      value={inquiryForm.destination_country}
                      onChange={(name) => setInquiryForm({ ...inquiryForm, destination_country: name })}
                      placeholder="Delivery to..."
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="delivery_date">Required Delivery Date</Label>
                  <Input
                    id="delivery_date"
                    type="date"
                    value={inquiryForm.required_delivery_date}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, required_delivery_date: e.target.value })}
                    data-testid="inquiry-delivery-date-input"
                  />
                </div>
                
                <div>
                  <PaymentTermsSelector
                    value={inquiryForm.preferred_payment_terms}
                    onChange={(terms) => setInquiryForm({ ...inquiryForm, preferred_payment_terms: terms })}
                    showNegotiableOption={false}
                    showCustomOption={false}
                    showSafetyNotice={false}
                    maxSelections={3}
                    label="Preferred Payment Terms"
                    placeholder="Select your preferred terms..."
                    role="buyer"
                  />
                </div>
                
                <div>
                  <Label htmlFor="proposed_terms">Additional Payment Terms (Optional)</Label>
                  <Textarea
                    id="proposed_terms"
                    value={inquiryForm.proposed_payment_terms}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, proposed_payment_terms: e.target.value })}
                    placeholder="Any specific payment arrangements you'd like to propose..."
                    className="h-16"
                    data-testid="inquiry-proposed-terms-input"
                  />
                </div>
                
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inquiryForm.sample_required}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, sample_required: e.target.checked })}
                      className="rounded border-gray-300"
                      data-testid="inquiry-sample-checkbox"
                    />
                    <span className="text-sm">Sample required before order</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inquiryForm.inspection_required}
                      onChange={(e) => setInquiryForm({ ...inquiryForm, inspection_required: e.target.checked })}
                      className="rounded border-gray-300"
                      data-testid="inquiry-inspection-checkbox"
                    />
                    <span className="text-sm">Third-party inspection required</span>
                  </label>
                </div>
                
                {/* High-value order warning */}
                {parseFloat(inquiryForm.estimated_order_value) > 10000 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Trade Safety Tip:</strong> For new international counterparties, consider using a Letter of Credit, 
                    escrow through a licensed provider, partial advance payment, or independent inspection before shipment.
                  </div>
                )}
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading} data-testid="submit-inquiry-btn">
                {isLoading ? 'Sending...' : 'Send Inquiry'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Product Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { resetProductForm(); setEditingProduct(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Product</DialogTitle>
              <DialogDescription>
                Update your product details
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditProduct} className="space-y-4">
              {/* Image Upload */}
              <div>
                <Label>Product Images</Label>
                <div className="mt-2">
                  <div className="flex flex-wrap gap-3 mb-3">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={preview} 
                          alt={`Preview ${index + 1}`} 
                          className="w-20 h-20 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                    >
                      <Image className="h-5 w-5 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">Add</span>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    multiple
                  />
                </div>
              </div>
              
              {/* Video Upload in Edit */}
              <div>
                <Label>Product Videos</Label>
                <div className="mt-2">
                  <div className="flex flex-wrap gap-3 mb-3">
                    {videoPreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        <video 
                          src={preview} 
                          className="w-28 h-20 object-cover rounded-lg border bg-black"
                          muted
                        />
                        <button
                          type="button"
                          onClick={() => removeVideo(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="h-6 w-6 text-white/80" />
                        </div>
                      </div>
                    ))}
                    <div 
                      onClick={() => videoInputRef.current?.click()}
                      className="w-28 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                    >
                      <Video className="h-5 w-5 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">Add Video</span>
                    </div>
                  </div>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                    multiple
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-title">Product Title</Label>
                  <Input
                    id="edit-title"
                    value={productForm.title}
                    onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-category">Category</Label>
                  <Select 
                    value={productForm.category} 
                    onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {exportCategories.map((group) => (
                        <div key={group.group}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                            {group.group}
                          </div>
                          {group.items.map((item) => (
                            <SelectItem key={item} value={item}>
                              {item}
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  required
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="edit-price">Price</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    step="0.01"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-currency">Currency</Label>
                  <Select value={productForm.currency || 'INR'} onValueChange={(v) => setProductForm({ ...productForm, currency: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">₹ INR</SelectItem>
                      <SelectItem value="USD">$ USD</SelectItem>
                      <SelectItem value="EUR">€ EUR</SelectItem>
                      <SelectItem value="GBP">£ GBP</SelectItem>
                      <SelectItem value="AED">د.إ AED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-min-order">Min Order</Label>
                  <Input
                    id="edit-min-order"
                    type="number"
                    value={productForm.min_order}
                    onChange={(e) => setProductForm({ ...productForm, min_order: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-unit">Unit</Label>
                  <Select value={productForm.unit} onValueChange={(v) => setProductForm({ ...productForm, unit: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">Kg</SelectItem>
                      <SelectItem value="ton">Ton</SelectItem>
                      <SelectItem value="pieces">Pieces</SelectItem>
                      <SelectItem value="meters">Meters</SelectItem>
                      <SelectItem value="liters">Liters</SelectItem>
                      <SelectItem value="sets">Sets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowEditDialog(false); resetProductForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-destructive">Delete Product</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{editingProduct?.title}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteProduct} disabled={isDeleting}>
                {isDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Delete Product'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Description Generator Modal */}
        <Dialog open={showAiModal} onOpenChange={setShowAiModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                AI Description Generator
              </DialogTitle>
              <DialogDescription>
                Provide details about your product and let AI generate a professional description
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="ai-product-name" className="flex items-center gap-1">
                  Product Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="ai-product-name"
                  value={aiModalForm.productName}
                  onChange={(e) => setAiModalForm({ ...aiModalForm, productName: e.target.value })}
                  placeholder="e.g., Premium Basmati Rice - Extra Long Grain"
                  className="mt-1"
                  data-testid="ai-modal-product-name"
                />
              </div>

              <div>
                <Label htmlFor="ai-product-details" className="flex items-center gap-1">
                  Product Details/Specs <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="ai-product-details"
                  value={aiModalForm.productDetails}
                  onChange={(e) => setAiModalForm({ ...aiModalForm, productDetails: e.target.value })}
                  placeholder="Enter key details: material, size, weight, grade, certifications, MOQ, etc."
                  className="mt-1"
                  rows={4}
                  data-testid="ai-modal-product-details"
                />
              </div>

              <div>
                <Label htmlFor="ai-hs-code">HS Code (Optional)</Label>
                <Input
                  id="ai-hs-code"
                  value={aiModalForm.hsCode}
                  onChange={(e) => setAiModalForm({ ...aiModalForm, hsCode: e.target.value })}
                  placeholder="e.g., 1006.30"
                  className="mt-1"
                  data-testid="ai-modal-hs-code"
                />
              </div>

              {/* AI Generated Result */}
              {aiSuggestions?.description && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-800 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Generated Description
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateDescription}
                        disabled={isGeneratingDescription}
                        className="text-xs"
                      >
                        <RefreshCw className={`h-3 w-3 mr-1 ${isGeneratingDescription ? 'animate-spin' : ''}`} />
                        Regenerate
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setAiSuggestions(null)}
                        className="text-xs"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-green-700 whitespace-pre-wrap">
                    {aiSuggestions.description}
                  </p>
                  {aiSuggestions.keywords?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-green-600">
                        <span className="font-medium">Keywords:</span> {aiSuggestions.keywords.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAiModal(false)}>
                Cancel
              </Button>
              {aiSuggestions?.description ? (
                <Button onClick={handleApplyAiDescription} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Apply Description
                </Button>
              ) : (
                <Button 
                  onClick={handleGenerateFromModal}
                  disabled={isGeneratingDescription || !aiModalForm.productName || !aiModalForm.productDetails}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  {isGeneratingDescription ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" /> Generate</>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}